import { z } from 'zod';

/**
 * Building an endorse form out of the schema the policy's PIN resolved to.
 *
 * # Why the schema is fetched rather than known
 *
 * A policy's payload validates against the schema its pin named, forever — never against the
 * latest published one (architecture §B.5). Member P-10442 is pinned to `insurance_core.v1`,
 * while `v2` is the newest published asset and adds `lock_certified`. A console that rendered a
 * form from "the current product schema" would offer that field, the operator would fill it in,
 * and the write would be refused — after the work.
 *
 * So the form is DERIVED from the document the service returned. No field is hard-coded, and a
 * schema that gains a field gains an input without this file changing.
 *
 * # Why it reads one level of nesting
 *
 * Since SDD-010 (C-26) the policy-data schema is DERIVED from the core config, and its shape is
 * one property per insured object or question group, each an object holding that group's fields:
 * `{ bike: { bike_value, frame_number }, cover: { coverage_tier } }`. The form therefore reads the
 * groups and their scalar fields, and names a field by its path — `bike.bike_value` — so two
 * groups may both have a `name`. A multi object (an ARRAY of instances) has no form here yet and is
 * reported rather than skipped, because silently dropping a part the schema requires is how a form
 * produces a payload that cannot be written. A flat, pre-SDD-010 schema still reads: its scalars
 * land in the root group.
 */

export type FieldKind = 'string' | 'number' | 'integer' | 'boolean' | 'enum';

export interface PayloadField {
  /** The field's path in the payload: `bike.bike_value`, or a bare name at the root. */
  readonly name: string;
  /** The property name inside its group — what the input is labelled with. */
  readonly label: string;
  /** The insured object or question group the field belongs to; `''` at the root. */
  readonly group: string;
  readonly kind: FieldKind;
  readonly required: boolean;
  /** Present for `kind === 'enum'`. */
  readonly options?: readonly string[];
  /** From the schema, when it states them — rendered as the input's constraints. */
  readonly minimum?: number;
  readonly maximum?: number;
  readonly pattern?: string;
}

export interface PayloadForm {
  readonly fields: readonly PayloadField[];
  /** The groups, in the order their fields appear. */
  readonly groups: readonly string[];
  /** Groups the root schema requires — a payload without one of them is refused. */
  readonly requiredGroups: readonly string[];
  /** Properties the reader could not turn into inputs, named so the gap is visible. */
  readonly unsupported: readonly string[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const stringList = (value: unknown): Set<string> =>
  new Set(
    (Array.isArray(value) ? value : []).filter((name): name is string => typeof name === 'string'),
  );

function readField(path: string, label: string, group: string, raw: unknown, required: boolean): PayloadField | null {
  const property = asRecord(raw) as {
    type?: unknown;
    enum?: unknown;
    minimum?: unknown;
    maximum?: unknown;
    pattern?: unknown;
  };
  const type: unknown = property.type;
  const options: unknown = property.enum;

  const numeric = (value: unknown): number | undefined =>
    typeof value === 'number' ? value : undefined;
  const bounds = {
    ...(numeric(property.minimum) === undefined ? {} : { minimum: numeric(property.minimum) }),
    ...(numeric(property.maximum) === undefined ? {} : { maximum: numeric(property.maximum) }),
    ...(typeof property.pattern === 'string' ? { pattern: property.pattern } : {}),
  };

  if (Array.isArray(options)) {
    const values = options.filter((option): option is string => typeof option === 'string');
    if (values.length === 0) return null;
    return { name: path, label, group, kind: 'enum', required, options: values, ...bounds };
  }
  if (type === 'string' || type === 'number' || type === 'integer' || type === 'boolean') {
    return { name: path, label, group, kind: type, required, ...bounds };
  }
  return null;
}

const sortedKeys = (record: Record<string, unknown>): string[] =>
  // Sorted: a form whose field order depends on JSON key order changes shape between two
  // responses that mean the same thing.
  Object.keys(record).sort((a, b) => a.localeCompare(b));

export function payloadFormOf(schema: unknown): PayloadForm {
  const document = asRecord(schema) as { properties?: unknown; required?: unknown };
  const properties = asRecord(document.properties);
  const rootRequired = stringList(document.required);

  const fields: PayloadField[] = [];
  const groups: string[] = [];
  const requiredGroups: string[] = [];
  const unsupported: string[] = [];

  for (const name of sortedKeys(properties)) {
    const property = asRecord(properties[name]) as { type?: unknown; properties?: unknown; required?: unknown };
    if (property.type === 'object') {
      const nested = asRecord(property.properties);
      const nestedKeys = sortedKeys(nested);
      if (nestedKeys.length === 0) {
        unsupported.push(name);
        continue;
      }
      groups.push(name);
      if (rootRequired.has(name)) requiredGroups.push(name);
      const nestedRequired = stringList(property.required);
      for (const key of nestedKeys) {
        const field = readField(`${name}.${key}`, key, name, nested[key], nestedRequired.has(key));
        if (field === null) unsupported.push(`${name}.${key}`);
        else fields.push(field);
      }
      continue;
    }
    // A scalar at the root — the flat, pre-SDD-010 shape — or an array (a multi object), which
    // has no form here yet and is named as such.
    const field = readField(name, name, '', properties[name], rootRequired.has(name));
    if (field === null) unsupported.push(name);
    else {
      fields.push(field);
      if (!groups.includes('')) groups.push('');
    }
  }
  return { fields, groups, requiredGroups, unsupported };
}

function fieldSchema(field: PayloadField): z.ZodType {
  let schema: z.ZodType;
  switch (field.kind) {
    case 'boolean':
      schema = z.boolean();
      break;
    case 'number':
    case 'integer': {
      let numeric = field.kind === 'integer' ? z.int() : z.number();
      if (field.minimum !== undefined) numeric = numeric.min(field.minimum, { error: 'range' });
      if (field.maximum !== undefined) numeric = numeric.max(field.maximum, { error: 'range' });
      schema = numeric;
      break;
    }
    case 'enum':
      schema = z.enum([...(field.options ?? [])] as [string, ...string[]]);
      break;
    default: {
      let text = z.string();
      if (field.pattern !== undefined) {
        text = text.regex(new RegExp(field.pattern), { error: 'pattern' });
      }
      schema = text;
    }
  }
  return field.required ? schema : schema.optional();
}

/**
 * A Zod schema for the form, built from the same reading.
 *
 * It mirrors the constraints the published schema states — a number's range, a string's pattern,
 * one object per group — so an operator is told at the field rather than by the service. It is
 * deliberately NOT the authority: the service revalidates against the pinned schema on every
 * write, and it is the one that decides.
 */
export function payloadSchemaOf(form: PayloadForm): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodType> = {};
  for (const group of form.groups) {
    const members = form.fields.filter((field) => field.group === group);
    if (group === '') {
      for (const field of members) shape[field.name] = fieldSchema(field);
      continue;
    }
    const nested: Record<string, z.ZodType> = {};
    for (const field of members) nested[field.label] = fieldSchema(field);
    // `strict`: the derived schema says `additionalProperties: false` at every level, and a form
    // that let an extra key through would be authoring a payload the write path refuses.
    const object = z.object(nested).strict();
    shape[group] = form.requiredGroups.includes(group) ? object : object.optional();
  }
  return z.object(shape).strict();
}

/**
 * Coerces the strings the inputs produce into the payload the schema describes — typed scalars
 * inside their groups.
 *
 * HTML inputs hand back strings; a schema saying `number` means a JSON number, and sending "3200"
 * where 3200 is required is a payload that fails validation for a reason no operator can see.
 */
export function coercePayload(
  form: PayloadForm,
  values: Readonly<Record<string, string>>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of form.fields) {
    const raw = values[field.name];
    if (raw === undefined || raw === '') continue;
    let value: unknown;
    switch (field.kind) {
      case 'boolean':
        value = raw === 'true';
        break;
      case 'number':
      case 'integer': {
        const parsed = Number(raw);
        // NaN is passed through rather than dropped: the schema is what says the value is wrong,
        // and dropping it would turn "not a number" into "missing", which is a different error.
        value = Number.isNaN(parsed) ? raw : parsed;
        break;
      }
      default:
        value = raw;
    }
    if (field.group === '') {
      out[field.name] = value;
      continue;
    }
    const group = asRecord(out[field.group]);
    out[field.group] = { ...group, [field.label]: value };
  }
  return out;
}

/** The current payload as form values — the starting point for an endorsement. */
export function payloadValues(data: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  const scalar = (value: unknown): string | undefined => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return undefined;
  };
  for (const [key, value] of Object.entries(asRecord(data))) {
    const flat = scalar(value);
    if (flat !== undefined) {
      out[key] = flat;
      continue;
    }
    // One level of groups. An array (a multi object) has no inputs here — see `payloadFormOf`.
    if (Array.isArray(value)) continue;
    for (const [inner, innerValue] of Object.entries(asRecord(value))) {
      const text = scalar(innerValue);
      // Only scalars: a value the schema describes as an object has no text input to carry it,
      // and stringifying one would put "[object Object]" into a form field.
      if (text !== undefined) out[`${key}.${inner}`] = text;
    }
  }
  return out;
}

/** The form-value key a Zod issue is about: `bike.bike_value` for the path `['bike', 'bike_value']`. */
export const fieldNameOfIssuePath = (path: readonly PropertyKey[]): string =>
  path.filter((segment): segment is string => typeof segment === 'string').join('.');
